import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import ScrollToTop from "@/components/ScrollToTop";
import NotFound from "@/pages/NotFound";
import { APP_SITE } from "@/lib/links";

// This is the MARKETING fork — danberrymusic.com. The studio, the harp, the
// library, shared scores and the live events live in the app fork
// (jewishmusiclab.com). See /app/memory/SPLIT_PLAN.md.

// ---------- auth (admin sign-in) ----------
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";

// ---------- the brand site ----------
import Home from "@/pages/Home";
import Faq from "@/pages/Faq";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Podcast from "@/pages/Podcast";
import PodcastEpisode from "@/pages/PodcastEpisode";
import About from "@/pages/About";
import Music from "@/pages/Music";
import Performances from "@/pages/Performances";
import CommunityEvents from "@/pages/CommunityEvents";
import Patrons from "@/pages/Patrons";
import PatronWelcome from "@/pages/PatronWelcome";
import Soundbaths from "@/pages/Soundbaths";
import InteractiveConcerts from "@/pages/InteractiveConcerts";
import Galas from "@/pages/Galas";
import Lifecycle from "@/pages/Lifecycle";
import Commissions from "@/pages/Commissions";
import Notes from "@/pages/Notes";
import Contact from "@/pages/Contact";
import ThankYou from "@/pages/ThankYou";
// Admin: podcast, announcement, venues
import AdminHome from "@/pages/AdminHome";
import PodcastAdmin from "@/pages/PodcastAdmin";
import AnnouncementAdmin from "@/pages/AnnouncementAdmin";
import VenuesAdmin from "@/pages/VenuesAdmin";

/** Hands an old app URL (share links, QR codes) over to the app domain. */
function ToApp({ path = "" }) {
  const params = useParams();
  const location = useLocation();
  const resolved = path.replace(/:(\w+)/g, (_, key) => params[key] || "");
  React.useEffect(() => {
    window.location.replace(`${APP_SITE}${resolved}${location.search}`);
  }, [resolved, location.search]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  // Catch the OAuth fragment synchronously during render — a useEffect would run
  // after the admin page has already asked for a sign-in.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* ================= auth ================= */}
        <Route path="/app/login" element={<Login />} />
        <Route path="/app/auth/callback" element={<AuthCallback />} />

        {/* ================= brand site ================= */}
        <Route path="/" element={<Home />} />

        {/* The Alephonics method + demo live on jewishmusiclab.com now. Only
            the podcast stayed on this site. */}
        <Route path="/alephonics" element={<Navigate to="/podcast" replace />} />
        <Route path="/alephonics/method" element={<Navigate to="/podcast" replace />} />
        <Route path="/the-method" element={<Navigate to="/podcast" replace />} />
        <Route path="/alephonics/try-it" element={<Navigate to="/podcast" replace />} />
        <Route path="/try-it" element={<Navigate to="/podcast" replace />} />
        <Route path="/hear-a-word" element={<Navigate to="/podcast" replace />} />
        <Route path="/podcast" element={<Podcast />} />
        <Route path="/podcast/:slug" element={<PodcastEpisode />} />
        <Route path="/alephonics/podcast" element={<Navigate to="/podcast" replace />} />
        <Route path="/alephonics/podcast/:slug" element={<PodcastEpisode />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/about" element={<About />} />
        <Route path="/music" element={<Music />} />
        <Route path="/patrons" element={<Patrons />} />
        {/* Stripe checkout returns new patrons here */}
        <Route path="/patrons/welcome" element={<PatronWelcome />} />
        {/* Listening Club retired — patronage replaced it */}
        <Route path="/club" element={<Navigate to="/patrons" replace />} />

        {/* Bookings — upcoming performances, private events, community events */}
        <Route path="/performances" element={<Performances />} />
        <Route path="/community-events" element={<CommunityEvents />} />
        <Route path="/private-events" element={<Lifecycle />} />
        <Route path="/tour" element={<Navigate to="/performances" replace />} />
        <Route path="/live" element={<Navigate to="/performances" replace />} />
        <Route path="/soundbaths" element={<Soundbaths />} />
        <Route path="/events" element={<Navigate to="/community-events" replace />} />
        {/* Canonical "Book a concert" page — this is the centerpiece offering */}
        <Route path="/book" element={<InteractiveConcerts />} />
        {/* Legacy alias so old links keep working */}
        <Route path="/events/interactive-concerts" element={<Navigate to="/book" replace />} />
        <Route path="/events/galas" element={<Galas />} />
        <Route path="/events/lifecycle" element={<Navigate to="/private-events" replace />} />
        <Route path="/commissions" element={<Commissions />} />
        <Route path="/notes" element={<Notes />} />
        <Route path="/contact" element={<Contact />} />
        {/* Compose Yourself! waitlist retired — community events replaced it */}
        <Route path="/waitlist" element={<Navigate to="/community-events" replace />} />
        <Route path="/thank-you" element={<ThankYou />} />

        {/* admin: podcast, announcement, venues */}
        <Route path="/podcast-admin" element={<Navigate to="/admin/podcast" replace />} />
        <Route path="/admin" element={<AdminHome />} />
        <Route path="/admin/podcast" element={<PodcastAdmin />} />
        <Route path="/admin/announcement" element={<AnnouncementAdmin />} />
        <Route path="/admin/venues" element={<VenuesAdmin />} />

        {/* ======= moved to the app domain — old links and QR codes ======= */}
        <Route path="/app" element={<ToApp path="/app/hebrew" />} />
        <Route path="/app/hebrew" element={<ToApp path="/app/hebrew" />} />
        <Route path="/app/harp" element={<ToApp path="/app/harp" />} />
        <Route path="/app/library" element={<ToApp path="/app/library" />} />
        <Route path="/composition/:id" element={<ToApp path="/composition/:id" />} />
        <Route path="/live/:slug" element={<ToApp path="/live/:slug" />} />
        <Route path="/live/:slug/admin" element={<ToApp path="/live/:slug/admin" />} />
        <Route path="/live/:slug/display" element={<ToApp path="/live/:slug/display" />} />
        <Route path="/live/:slug/check" element={<ToApp path="/live/:slug/check" />} />
        <Route path="/admin/live-events" element={<ToApp path="/admin/live-events" />} />
        <Route path="/live-admin" element={<ToApp path="/admin/live-events" />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
